/**
 * CreateCenterPage.jsx - Aggiunta Centro (Crea o Ricevi Trasferimento)
 * Il Diario di Bordo - SaaS
 * 
 * FASE 6B: Pagina completa per:
 * - Creare un nuovo centro da zero
 * - Ricevere un centro da un'altra organizzazione (Transfer Code)
 * - Visualizzare e gestire richieste di trasferimento in arrivo
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationApi } from '../../services/organizationApi'
import { Card } from '../../components/ui'

import {
  Building2,
  Plus,
  ArrowDownToLine,
  Loader2,
  Copy,
  Check,
  X,
  RefreshCw,
  Shield,
  Inbox,
  Baby,
  Users,
  Clock,
  AlertTriangle,
  KeyRound,
  ArrowLeft,
  Trash2,
  FileText,
  CheckCircle
} from 'lucide-react'

export default function CreateCenterPage() {
  // null = scelta iniziale, 'create' = form creazione, 'transfer' = flusso trasferimento
  const [mode, setMode] = useState(null)
  
  const navigate = useNavigate()
  
  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link 
          to="/organization/centers" 
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna ai centri
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
          Aggiungi Centro
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Crea un nuovo centro o ricevi un trasferimento da un'altra organizzazione
        </p>
      </div>
      
      {/* Scelta modalità */}
      {mode === null && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setMode('create')}
            className="flex flex-col items-center gap-4 p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 transition-colors text-left group"
          >
            <div className="p-4 rounded-full bg-primary-100 dark:bg-primary-900/30 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/50 transition-colors">
              <Plus className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Crea Nuovo Centro
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Crea un centro educativo da zero per la tua organizzazione
              </p>
            </div>
          </button>
          
          <button
            onClick={() => setMode('transfer')}
            className="flex flex-col items-center gap-4 p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors text-left group"
          >
            <div className="p-4 rounded-full bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
              <ArrowDownToLine className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Ricevi Trasferimento
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Ricevi un centro esistente da un'altra organizzazione
              </p>
            </div>
          </button>
        </div>
      )}
      
      {/* Form creazione nuovo centro */}
      {mode === 'create' && (
        <CreateNewCenterForm 
          onBack={() => setMode(null)} 
          onSuccess={() => navigate('/organization/centers')} 
        />
      )}
      
      {/* Flusso trasferimento */}
      {mode === 'transfer' && (
        <TransferReceiveFlow 
          onBack={() => setMode(null)} 
          onSuccess={() => navigate('/organization/centers')} 
        />
      )}
    </div>
  )
}


// ============================================
// FORM CREAZIONE NUOVO CENTRO
// ============================================

function CreateNewCenterForm({ onBack, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    entityType: 'cooperativa',
    contacts: { email: '', phone: '' },
    address: { street: '', city: '', province: '', postalCode: '' }
  })
  
  const { showSuccess, showError } = useNotification()
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await organizationApi.createCenter(formData)
      showSuccess('Centro creato con successo!')
      onSuccess()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella creazione del centro')
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div>
      <button
        onClick={onBack}
        className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Cambia modalità
      </button>
      
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Building2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nuovo Centro</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nome e Tipo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome Centro *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                minLength={3}
                maxLength={100}
                className="input"
                placeholder="Es. Centro Educativo Roma Nord"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo Ente *
              </label>
              <select
                value={formData.entityType}
                onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                required
                className="input"
              >
                <option value="cooperativa">Cooperativa</option>
                <option value="onlus">ONLUS</option>
                <option value="privato">Privato</option>
                <option value="pubblico">Pubblico</option>
              </select>
            </div>
          </div>
          
          {/* Contatti */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
              <input
                type="email"
                value={formData.contacts.email}
                onChange={(e) => setFormData({ 
                  ...formData, contacts: { ...formData.contacts, email: e.target.value }
                })}
                required
                className="input"
                placeholder="email@centro.it"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefono</label>
              <input
                type="tel"
                value={formData.contacts.phone}
                onChange={(e) => setFormData({ 
                  ...formData, contacts: { ...formData.contacts, phone: e.target.value }
                })}
                className="input"
                placeholder="+39 xxx xxx xxxx"
              />
            </div>
          </div>
          
          {/* Indirizzo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Indirizzo</label>
            <div className="space-y-3">
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => setFormData({ 
                  ...formData, address: { ...formData.address, street: e.target.value }
                })}
                className="input"
                placeholder="Via/Piazza"
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({ 
                      ...formData, address: { ...formData.address, city: e.target.value }
                    })}
                    className="input"
                    placeholder="Città"
                  />
                </div>
                <input
                  type="text"
                  value={formData.address.province}
                  onChange={(e) => setFormData({ 
                    ...formData, address: { ...formData.address, province: e.target.value.toUpperCase() }
                  })}
                  maxLength={2}
                  className="input"
                  placeholder="Prov."
                />
                <input
                  type="text"
                  value={formData.address.postalCode}
                  onChange={(e) => setFormData({ 
                    ...formData, address: { ...formData.address, postalCode: e.target.value }
                  })}
                  maxLength={5}
                  className="input"
                  placeholder="CAP"
                />
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onBack} className="btn-secondary">Annulla</button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creazione...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" />Crea Centro</>
              )}
            </button>
          </div>
        </form>
      </Card>
    </div>
  )
}


// ============================================
// FLUSSO RICEVI TRASFERIMENTO
// ============================================

function TransferReceiveFlow({ onBack, onSuccess }) {
  const [loadingCodes, setLoadingCodes] = useState(true)
  const [loadingTransfers, setLoadingTransfers] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [transferCodes, setTransferCodes] = useState([])
  const [incomingTransfers, setIncomingTransfers] = useState([])
  const [codeCopied, setCodeCopied] = useState(null)
  const [transferLoading, setTransferLoading] = useState(false)
  
  const { showSuccess, showError } = useNotification()
  
  // Carica codici attivi e richieste in arrivo
  const loadData = useCallback(async () => {
    try {
      setLoadingCodes(true)
      setLoadingTransfers(true)
      
      const [codesRes, transfersRes] = await Promise.all([
        organizationApi.getTransferCodes().catch(() => ({ data: { transferCodes: [] } })),
        organizationApi.getIncomingTransfers().catch(() => ({ data: { requests: [] } }))
      ])
      
      setTransferCodes(codesRes.data.transferCodes || [])
      setIncomingTransfers(transfersRes.data.requests || [])
    } catch (err) {
      console.error('Error loading transfer data:', err)
    } finally {
      setLoadingCodes(false)
      setLoadingTransfers(false)
    }
  }, [])
  
  useEffect(() => { loadData() }, [loadData])
  
  // Genera nuovo codice
  const handleGenerateCode = async () => {
    setGenerating(true)
    try {
      const res = await organizationApi.generateTransferCode()
      showSuccess('Codice di ricezione generato!')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella generazione del codice')
    } finally {
      setGenerating(false)
    }
  }
  
  // Revoca codice
  const handleRevokeCode = async (code) => {
    try {
      await organizationApi.revokeTransferCode(code)
      showSuccess('Codice revocato')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nella revoca')
    }
  }
  
  // Copia codice
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
    setCodeCopied(code)
    setTimeout(() => setCodeCopied(null), 2000)
  }
  
  // Approva trasferimento
  const handleApproveTransfer = async (requestId) => {
    setTransferLoading(true)
    try {
      await organizationApi.approveTransfer(requestId)
      showSuccess('Trasferimento approvato! Il centro è ora nella tua organizzazione.')
      onSuccess()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'approvazione')
    } finally {
      setTransferLoading(false)
    }
  }
  
  // Rifiuta trasferimento
  const handleRejectTransfer = async (requestId, reason) => {
    setTransferLoading(true)
    try {
      await organizationApi.rejectTransfer(requestId, reason)
      showSuccess('Richiesta rifiutata')
      loadData()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nel rifiuto')
    } finally {
      setTransferLoading(false)
    }
  }
  
  const activeCodes = transferCodes.filter(c => c.isValid !== false)
  
  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm flex items-center gap-1"
      >
        <ArrowLeft className="h-4 w-4" />
        Cambia modalità
      </button>
      
      {/* Spiegazione flusso */}
      <Card>
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
            <ArrowDownToLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Ricevi un Centro da un'altra Organizzazione
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Per ricevere un centro, segui questi passaggi:
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-bold flex items-center justify-center">1</span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Genera un codice di ricezione</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Il codice identifica la tua organizzazione come destinataria</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-bold flex items-center justify-center">2</span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Comunica il codice all'organizzazione cedente</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Inviagli il codice via email, telefono o di persona</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-bold flex items-center justify-center">3</span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Approva la richiesta quando arriva</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Riceverai una notifica e potrai approvare o rifiutare il trasferimento</p>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Sezione: Codici di ricezione */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Codici di Ricezione</h3>
          </div>
          <button
            onClick={handleGenerateCode}
            disabled={generating}
            className="btn-primary text-sm"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Generazione...</>
            ) : (
              <><Plus className="h-4 w-4 mr-1" />Genera Codice</>
            )}
          </button>
        </div>
        
        {loadingCodes ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : activeCodes.length === 0 ? (
          <div className="text-center py-8">
            <KeyRound className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Nessun codice attivo</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Genera un codice per ricevere un centro</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeCodes.map((tc) => (
              <div 
                key={tc.code}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-lg font-mono font-bold text-gray-900 dark:text-white tracking-wider">
                      {tc.code}
                    </code>
                    <button
                      onClick={() => handleCopyCode(tc.code)}
                      className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                      title="Copia codice"
                    >
                      {codeCopied === tc.code ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      Scade: {new Date(tc.expiresAt).toLocaleDateString('it-IT')}
                    </span>
                    {tc.daysRemaining !== undefined && (
                      <span className={`${tc.daysRemaining <= 7 ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}`}>
                        ({tc.daysRemaining} giorni)
                      </span>
                    )}
                    {tc.usageCount > 0 && (
                      <span className="flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" />
                        {tc.usageCount} utilizz{tc.usageCount === 1 ? 'o' : 'i'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeCode(tc.code)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-2"
                  title="Revoca codice"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
      
      {/* Sezione: Richieste in arrivo */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Richieste in Arrivo</h3>
            {incomingTransfers.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full">
                {incomingTransfers.length}
              </span>
            )}
          </div>
          <button
            onClick={loadData}
            disabled={loadingTransfers}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Aggiorna"
          >
            <RefreshCw className={`h-4 w-4 ${loadingTransfers ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {loadingTransfers ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : incomingTransfers.length === 0 ? (
          <div className="text-center py-8">
            <Inbox className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Nessuna richiesta in arrivo</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Quando un'organizzazione userà il tuo codice, la richiesta apparirà qui
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {incomingTransfers.map((request) => (
              <IncomingTransferCard
                key={request.id}
                request={request}
                onApprove={handleApproveTransfer}
                onReject={handleRejectTransfer}
                loading={transferLoading}
              />
            ))}
          </div>
        )}
      </Card>
      
      {/* Info GDPR */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
        <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <p className="font-medium">Nota sulla conformità GDPR</p>
          <p className="mt-1 text-amber-700 dark:text-amber-300">
            La responsabilità della raccolta dei consensi per il trasferimento dei dati dei minori 
            è a carico dell'organizzazione cedente (Titolare del trattamento). 
            Prima di approvare, assicurati che l'organizzazione cedente abbia ottenuto i consensi necessari.
          </p>
        </div>
      </div>
    </div>
  )
}


// ============================================
// CARD RICHIESTA TRASFERIMENTO IN ARRIVO
// ============================================

function IncomingTransferCard({ request, onApprove, onReject, loading }) {
  const [showRejectReason, setShowRejectReason] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [confirmApprove, setConfirmApprove] = useState(false)
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-5">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
          <Inbox className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {request.centerSnapshot?.name || request.centerName || 'Centro'}
            </h4>
            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
              In arrivo
            </span>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Da: <strong>{request.fromOrganization?.name}</strong>
          </p>
          
          {request.requestedBy && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Richiedente: {request.requestedBy.nome} {request.requestedBy.cognome}
            </p>
          )}
          
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
            {(request.centerSnapshot?.minoriCount != null) && (
              <span className="flex items-center gap-1">
                <Baby className="h-4 w-4" />
                {request.centerSnapshot.minoriCount} minori
              </span>
            )}
            {(request.centerSnapshot?.educatoriCount != null) && (
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {request.centerSnapshot.educatoriCount} educatori
              </span>
            )}
            {request.daysRemaining !== undefined && (
              <span className={`flex items-center gap-1 ${request.daysRemaining <= 3 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                <Clock className="h-4 w-4" />
                {request.daysRemaining} giorni rimanenti
              </span>
            )}
          </div>
          
          {request.notes && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
              "{request.notes}"
            </p>
          )}
          
          {/* Pulsanti azione */}
          {!showRejectReason && !confirmApprove && (
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setConfirmApprove(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                Approva
              </button>
              <button
                onClick={() => setShowRejectReason(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Rifiuta
              </button>
            </div>
          )}
          
          {/* Conferma approvazione */}
          {confirmApprove && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-green-700 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">Conferma approvazione</p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Il centro verrà trasferito alla tua organizzazione. 
                    Assicurati che l'organizzazione cedente abbia raccolto i consensi GDPR necessari.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onApprove(request.id)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Confermo, Approva
                </button>
                <button
                  onClick={() => setConfirmApprove(false)}
                  disabled={loading}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 font-medium transition-colors"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
          
          {/* Form motivo rifiuto */}
          {showRejectReason && (
            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Motivo del rifiuto (opzionale)"
                className="input"
                maxLength={500}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onReject(request.id, rejectReason)
                    setShowRejectReason(false)
                  }}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  Conferma Rifiuto
                </button>
                <button
                  onClick={() => { setShowRejectReason(false); setRejectReason('') }}
                  disabled={loading}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 font-medium transition-colors"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
